import React, { useEffect } from 'react'
import { logger } from '../../utils/logger'
import { KatalMetricsPublisher } from '../../utils/metrics'
import { Failure } from '../../common/constants'
import { useMetadataContext } from '../../contexts/MetadataContext'
import configMap from './config'
import { taktId, logWithTakt } from '../../utils/takt'
import { MountLocationType } from '@amzn/intro-widgets-api'

interface ElementProps {
  id: string
  mountLocation?: MountLocationType
}

const metricsPublisher = KatalMetricsPublisher.newChildActionPublisherForMethod('Element')
// The general Element component to return appropriate react components according to the config file
export const Element: React.FC<ElementProps> = ({ id, mountLocation }) => {
  const { introWidgets, isMobile } = useMetadataContext()

  useEffect(() => {
    if (dataTaktId !== undefined) {
      logWithTakt(dataTaktId, { event: 'load' })
    }
  }, [])

  // get metadata by the anchorId
  const introWidgetMetadata = introWidgets?.find((obj) => obj.anchorId === id)

  if (introWidgetMetadata === undefined) {
    logger.error('anchorId not found', { id })
    metricsPublisher.publishCounterMonitor(`AnchorIdNotFound:${id}`, 1)
    logWithTakt(id, { event: 'anchor_id_not_found_error' })
    return null
  }

  const { type, anchorId, ...restProps } = introWidgetMetadata

  // if type is not defined or is not supported, return nothing
  if (!type || !configMap[type]) {
    logger.error('widget type is undefined or not supported', { anchorId, type })
    metricsPublisher.publishCounterMonitor(`UnsupportedType:${type}`, 1)
    logWithTakt(anchorId, { event: 'widget_type_error' })
    return null
  }

  const dataTaktId = taktId(type, anchorId, isMobile)

  try {
    const Component = configMap[type].component
    const propsMap = configMap[type].props || {}
    const props = Object.keys(restProps).reduce(
      (acc, key) => {
        const value = restProps[key] // value of metadata from backend

        // if key is defined in configMap
        if (propsMap[key]) {
          if (typeof propsMap[key] === 'function') {
            return {
              ...acc,
              ...propsMap[key](value), // execute the function with value as its parameter
            }
          }

          return {
            ...acc,
            [propsMap[key]]: value, // replace props key name
          }
        }
        return {
          ...acc,
          [key]: value, // default behavior: do nothing
        }
      },
      { type, anchorId }
    )

    return <Component key={anchorId} isMobile={isMobile} dataTaktId={dataTaktId} mountLocation={mountLocation} {...props} />
  } catch (error) {
    logger.error(`react component config parsing error`, error as any, { anchorId, type })
    metricsPublisher.publishCounterMonitor(Failure, 1)
    logWithTakt(dataTaktId, { event: 'config_parsing_error' })
    return null
  }
}
